import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * useVersionCheck Hook
 *
 * Checks if there's a new version available for the current user.
 * Automatically checks on mount and provides manual refresh function.
 *
 * @returns {Object} Version check state and functions
 * @property {boolean} hasNewVersion - Whether a new version is available
 * @property {Array} newVersions - Array of version objects user hasn't seen
 * @property {string} currentVersion - Current app version
 * @property {string} lastSeenVersion - User's last seen version
 * @property {boolean} loading - Whether check is in progress
 * @property {string|null} error - Error message if check failed
 * @property {Function} checkVersion - Manual version check function
 */
const useVersionCheck = () => {
  const { authFetch, currentUser } = useAuth();
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [newVersions, setNewVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [lastSeenVersion, setLastSeenVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Check for new version
   */
  const checkVersion = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authFetch('/api/changelog/check');
      const data = await response.json();

      if (response.ok) {
        setHasNewVersion(data.has_new_version || false);
        setNewVersions(data.new_versions || []);
        setCurrentVersion(data.current_version || null);
        setLastSeenVersion(data.last_seen_version || '0.0.0');
      } else {
        setError(data.message || 'Fehler beim Prüfen der Version');
        // Set defaults on error
        setHasNewVersion(false);
        setNewVersions([]);
      }
    } catch (err) {
      setError('Netzwerkfehler beim Prüfen der Version');
      setHasNewVersion(false);
      setNewVersions([]);
    } finally {
      setLoading(false);
    }
  }, [authFetch, currentUser]);

  /**
   * Mark current version as seen
   */
  const markVersionSeen = useCallback(async (version = null) => {
    try {
      const body = version ? JSON.stringify({ version }) : null;
      const response = await authFetch('/api/changelog/mark-seen', {
        method: 'POST',
        headers: body ? { 'Content-Type': 'application/json' } : {},
        body
      });

      if (response.ok) {
        // Update local state
        setHasNewVersion(false);
        setNewVersions([]);
        if (version) {
          setLastSeenVersion(version);
        } else if (currentVersion) {
          setLastSeenVersion(currentVersion);
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error marking version as seen:', err);
      return false;
    }
  }, [authFetch, currentVersion]);

  /**
   * Check version on mount
   */
  useEffect(() => {
    if (currentUser) {
      checkVersion();
    }
  }, [currentUser, checkVersion]);

  return {
    hasNewVersion,
    newVersions,
    currentVersion,
    lastSeenVersion,
    loading,
    error,
    checkVersion,
    markVersionSeen
  };
};

export default useVersionCheck;
